var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Sutl } from '../../../../sutl';
import { CommandFormatter } from './CommandFormatter';
import { Timeouts } from './Timeouts';
import { isPlainAsyncHandler, isRegExpAsyncHandler, TransportError, } from './Transport';
import { UdpEmulator } from './UdpEmulator';
import { createUdpSocket } from './UdpSocket';
export const defaultUdpCommandSocketOptions = {
    idleTimeoutMs: 20000,
    isBinary: false,
    logReceivedData: false,
    sendTimeoutMs: 2000,
};
function formatTags(tags) {
    if (tags.length === 0)
        return '';
    return ` [${tags.join(', ')}]`;
}
export class UdpCommandSocket {
    constructor(logFactory, options, emulatorKey) {
        // bind callbacks
        this.logFactory = logFactory;
        this.asyncHandlerId = 1;
        this.asyncHandlers = {};
        this.checkAllHandlers = false;
        this.isProcessingMessage = false;
        this._isClosed = false;
        this.lastReceivedTime = Date.now();
        this.receivedMessages = [];
        this.retryAttempts = 0;
        this.queuedCommands = new Array();
        this.timeouts = new Timeouts();
        this.options = Object.assign(Object.assign({}, defaultUdpCommandSocketOptions), { parser: (address, port, command) => command });
        this.messageCallback = this.messageCallback.bind(this);
        this.onIdleTimeout = this.onIdleTimeout.bind(this);
        this.processReceivedMessages = this.processReceivedMessages.bind(this);
        this.sendNextCommand = this.sendNextCommand.bind(this);
        this.sendTimeoutCallback = this.sendTimeoutCallback.bind(this);
        this.suspendTimeoutCallback = this.suspendTimeoutCallback.bind(this);
        this.errorCallback = this.errorCallback.bind(this);
        // initialization
        this.idleTimeout = this.timeouts.create();
        this.options = Object.assign(this.options, options !== null && options !== void 0 ? options : {});
        this.log = logFactory.channel('UdpCommandSocket');
        this.driverLog = logFactory.channel('UdpCommandSocket', 'driver');
        this.sendTimeout = this.timeouts.create();
        if (emulatorKey) {
            this._emulator = new UdpEmulator(emulatorKey, logFactory, this.options.isBinary);
            this.socket = this._emulator;
            this._suspendType = undefined;
        }
        else {
            this.socket = createUdpSocket();
        }
        this.socket.onMessage(this.messageCallback);
        this.socket.onError(this.errorCallback);
        this.suspendTimeout = this.timeouts.create();
        this.log.info(`created udp socket`);
    }
    // getters
    get emulator() {
        var _a;
        return (_a = this._emulator) === null || _a === void 0 ? void 0 : _a.emulator;
    }
    get host() {
        return this._host;
    }
    get isClosed() {
        return this._isClosed;
    }
    get isSuspended() {
        return this._suspendType !== undefined;
    }
    get port() {
        return this.options.port;
    }
    get suspendType() {
        return this._suspendType;
    }
    // methods
    bind(port, address) {
        return __awaiter(this, void 0, void 0, function* () {
            port = port !== null && port !== void 0 ? port : 0;
            if (port !== undefined) {
                if (address !== undefined) {
                    this.log.info(`binding to ${address}:${port}`);
                    yield this.socket.bind(port, address);
                }
                else {
                    this.log.info(`binding to ${port}`);
                    yield this.socket.bind(port);
                }
            }
            const localAddress = yield this.socket.getAddress();
            this.log.info(`bound to ${localAddress.address}:${localAddress.port}`);
        });
    }
    close() {
        this.currentCommand = undefined;
        this._host = undefined;
        this.isProcessingMessage = false;
        this.queuedCommands.length = 0;
        this.receivedMessages.length = 0;
        this._suspendType = undefined;
        this.timeouts.clearAll();
        if (this._isClosed) {
            this.log.error('already closed');
        }
        else {
            this.socket.close();
        }
        this._isClosed = true;
    }
    connect(host, username, password) {
        return __awaiter(this, void 0, void 0, function* () {
            this._host = host;
        });
    }
    errorCallback(error) {
        this.log.warning(`udp error: ${error.message}`);
    }
    hasCommands(tag) {
        if (tag === undefined) {
            return this.queuedCommands.length > 0;
        }
        else if (typeof tag === 'string') {
            for (const qc of this.queuedCommands) {
                if (qc.tags.includes(tag))
                    return true;
            }
        }
        else {
            for (const tagElement of tag) {
                if (this.hasCommands(tagElement))
                    return true;
            }
        }
        return false;
    }
    messageCallback(data) {
        if (this.options.logReceivedData) {
            const msg = CommandFormatter.format(data.message, true);
            this.log.info(`received: ${data.address}:${data.port}: ${msg}`);
        }
        this.receivedMessages.push(data);
        this.processReceivedMessages();
    }
    onConnectionLost(connectionLostCallback) {
        this.connectionLostCallback = connectionLostCallback;
        return this;
    }
    onIdleTimeout() {
        var _a;
        this.log.info('idle timeout, disconnecting');
        this.close();
        (_a = this.connectionLostCallback) === null || _a === void 0 ? void 0 : _a.call(this);
    }
    processReceivedMessages() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this._isClosed || this.isProcessingMessage)
                return;
            if (this._suspendType === 'all')
                return;
            const message = this.receivedMessages.shift();
            if (message === undefined) {
                this.isProcessingMessage = false;
                return;
            }
            if (this._suspendType === undefined && this.options.idleTimeoutMs > 0) {
                this.idleTimeout.set(this.onIdleTimeout, this.options.idleTimeoutMs);
            }
            const address = message.address;
            const port = message.port;
            const command = this.options.parser(message.address, message.port, message.message);
            const cmd = command !== null && command !== void 0 ? command : message.message;
            const fmtCmd = `${address}:${port}: ` +
                (typeof cmd === 'string'
                    ? CommandFormatter.format(cmd, this.options.isBinary)
                    : JSON.stringify(cmd));
            if (command === undefined) {
                this.driverLog.info(`ignoring: ${fmtCmd}`);
                this.isProcessingMessage = false;
                this.timeouts.set(this.processReceivedMessages); // prevent deep nesting
                return;
            }
            this.isProcessingMessage = true;
            this.lastReceivedTime = Date.now();
            let handled = false;
            // First, check current synchronous handler.
            if (this.currentCommand) {
                if (this.currentCommand.type === 'matchFunction') {
                    // address check built-in
                    const response = this.currentCommand.match({ address, port, command });
                    if (response !== undefined) {
                        const tags = formatTags(this.currentCommand.tags);
                        this.driverLog.info(`processing${tags}: ${fmtCmd}`);
                        this.currentCommand.resolve({ address, port, command: response });
                        handled = true;
                    }
                }
                else if (this.currentCommand.type === 'regExp') {
                    // address check built-in
                    const response = this.currentCommand.match({ address, port, command });
                    if (response !== undefined) {
                        const tags = formatTags(this.currentCommand.tags);
                        this.driverLog.info(`processing${tags}: ${fmtCmd}`);
                        this.currentCommand.resolve({ address, port, match: response });
                        handled = true;
                    }
                }
                else if (this.currentCommand.type === 'unmatched') {
                    // nothing to do
                }
                else {
                    this.log.error(`unhandled command type`);
                }
                if (handled) {
                    this.currentCommand = undefined;
                }
            }
            // If that didn't match, then check the asynchronous handlers.
            if (!handled) {
                for (const handler of Object.values(this.asyncHandlers)) {
                    if ((handler.address !== undefined && address !== handler.address) ||
                        (handler.port !== undefined && port !== handler.port)) {
                        continue;
                    }
                    if (isRegExpAsyncHandler(handler)) {
                        const response = handler.regExp.exec(command);
                        if (response) {
                            const name = handler.name ? ` [${handler.name}]` : '';
                            this.driverLog.info(`processing${name}: ${fmtCmd}`);
                            handler.callback(response, address, port);
                            handled = true;
                            if (!this.checkAllHandlers)
                                break;
                        }
                    }
                    else {
                        const response = handler.match(command);
                        if (response !== undefined) {
                            const name = handler.name ? ` [${handler.name}]` : '';
                            this.driverLog.info(`processing${name}: ${fmtCmd}`);
                            handler.callback(response, address, port);
                            handled = true;
                            if (!this.checkAllHandlers)
                                break;
                        }
                    }
                }
            }
            if (!handled) {
                this.driverLog.info(`unhandled: ${fmtCmd}`);
            }
            this.isProcessingMessage = false;
            this.timeouts.set(this.processReceivedMessages); // prevent deep nesting
            this.timeouts.set(this.sendNextCommand); // in case command sent while waiting for this one
        });
    }
    registerAsync(handlerOrRegExp, callback, name) {
        if (this._isClosed)
            this.log.error(`registerAsync: socket is closed`);
        const id = (this.asyncHandlerId++).toString();
        if (handlerOrRegExp instanceof RegExp) {
            if (callback === undefined)
                throw new Error('callback undefined');
            this.asyncHandlers[id] = {
                regExp: handlerOrRegExp,
                callback,
            };
            if (name)
                this.asyncHandlers[id].name = name;
        }
        else {
            if (isRegExpAsyncHandler(handlerOrRegExp) || isPlainAsyncHandler(handlerOrRegExp)) {
                this.asyncHandlers[id] = handlerOrRegExp;
            }
            else {
                throw new Error('unexpected async handler type');
            }
        }
        return id;
    }
    removeCommands(tag) {
        if (tag === undefined) {
            this.log.info('removed all commands');
            const error = new TransportError('removed all commands', 'commandRemoved');
            for (const qc of this.queuedCommands) {
                qc.reject(error);
            }
            this.queuedCommands.length = 0;
        }
        else if (typeof tag === 'string') {
            this.log.info(`removing: ${tag}`);
            const errorMessage = `removed command with tag: ${tag}`;
            Sutl.filterInPlace(this.queuedCommands, qc => {
                if (qc.tags.includes(tag)) {
                    qc.reject(new TransportError(errorMessage, 'commandRemoved'));
                    return false;
                }
                return true;
            });
        }
        else {
            for (const tagElement of tag) {
                this.removeCommands(tagElement);
            }
        }
    }
    resume() {
        this.suspendTimeout.clear();
        this.suspendTimeoutCallback();
    }
    send(command, regExpOrFn, tagsOrOpts, opts) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this._host === undefined)
                throw new Error(`host undefined`);
            if (this.options.port === undefined)
                throw new Error(`port undefined`);
            return this.sendInternal(this._host, this.options.port, command, regExpOrFn, tagsOrOpts, opts);
        });
    }
    sendAsync(command, tagsOrOpts, opts) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this._host === undefined)
                throw new Error(`host undefined`);
            if (this.options.port === undefined)
                throw new Error(`port undefined`);
            return this.sendInternalAsync(this._host, this.options.port, command, tagsOrOpts, opts);
        });
    }
    sendTo(address, port, command, regExpOrFn, tagsOrOpts, opts) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.sendInternal(address, port, command, regExpOrFn, tagsOrOpts, opts);
        });
    }
    sendToAsync(address, port, command, tagsOrOpts, opts) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.sendInternalAsync(address, port, command, tagsOrOpts, opts);
        });
    }
    sendInternal(address, port, command, regExpOrFn, tagsOrOpts, opts) {
        return __awaiter(this, void 0, void 0, function* () {
            let tags = [];
            if (typeof tagsOrOpts === 'string') {
                tags.push(tagsOrOpts);
            }
            else if (Array.isArray(tagsOrOpts)) {
                tags = tagsOrOpts;
            }
            else {
                opts = tagsOrOpts;
            }
            if (this._isClosed) {
                const msg = `can't send while closed` +
                    `, tags = ${JSON.stringify(tags)}` +
                    `, address = ${address}` +
                    `, port = ${port}` +
                    `, data = ${CommandFormatter.format(command, this.options.isBinary)}`;
                this.log.info(msg);
                throw new TransportError(msg, 'cantSend');
            }
            let promise;
            if (regExpOrFn instanceof RegExp) {
                promise = new Promise((resolve, reject) => {
                    var _a, _b;
                    const qc = {
                        address,
                        command,
                        match: cmd => {
                            if (cmd.address !== address)
                                return undefined;
                            const match = regExpOrFn.exec(cmd.command);
                            return match === null ? undefined : match;
                        },
                        port,
                        reject,
                        resolve,
                        retryAttempts: (_a = opts === null || opts === void 0 ? void 0 : opts.retryAttempts) !== null && _a !== void 0 ? _a : 0,
                        sendTimeoutMs: (_b = opts === null || opts === void 0 ? void 0 : opts.sendTimeoutMs) !== null && _b !== void 0 ? _b : this.options.sendTimeoutMs,
                        tags,
                        type: 'regExp',
                    };
                    this.queuedCommands.push(qc);
                    this.sendNextCommand();
                });
            }
            else {
                promise = new Promise((resolve, reject) => {
                    var _a, _b;
                    const qc = {
                        address,
                        command,
                        match: cmd => {
                            if (cmd.address !== address)
                                return undefined;
                            return regExpOrFn(cmd);
                        },
                        port,
                        reject,
                        resolve,
                        retryAttempts: (_a = opts === null || opts === void 0 ? void 0 : opts.retryAttempts) !== null && _a !== void 0 ? _a : 0,
                        sendTimeoutMs: (_b = opts === null || opts === void 0 ? void 0 : opts.sendTimeoutMs) !== null && _b !== void 0 ? _b : this.options.sendTimeoutMs,
                        tags,
                        type: 'matchFunction',
                    };
                    this.queuedCommands.push(qc);
                    this.sendNextCommand();
                });
            }
            if (this.queuedCommands.length > 0) {
                const cmd = CommandFormatter.format(command, this.options.isBinary);
                const strTags = tags.length > 0 ? ` [${tags.join(', ')}]` : '';
                this.driverLog.info(`queued${strTags}: ${address}:${port}: ${cmd}`);
            }
            return promise;
        });
    }
    sendInternalAsync(address, port, data, tagsOrOpts, opts) {
        return __awaiter(this, void 0, void 0, function* () {
            let tags = [];
            if (typeof tagsOrOpts === 'string') {
                tags.push(tagsOrOpts);
            }
            else if (Array.isArray(tagsOrOpts)) {
                tags = tagsOrOpts;
            }
            else {
                opts = tagsOrOpts;
            }
            if (this._isClosed) {
                throw new Error(`can't send while closed` +
                    `, tags = ${JSON.stringify(tags)}` +
                    `, address = ${address}` +
                    `, port = ${port}` +
                    `, data = ${CommandFormatter.format(data, this.options.isBinary)}`);
            }
            const promise = new Promise((resolve, reject) => {
                var _a, _b;
                const qc = {
                    address,
                    command: data,
                    port,
                    reject,
                    resolve,
                    retryAttempts: (_a = opts === null || opts === void 0 ? void 0 : opts.retryAttempts) !== null && _a !== void 0 ? _a : 0,
                    sendTimeoutMs: (_b = opts === null || opts === void 0 ? void 0 : opts.sendTimeoutMs) !== null && _b !== void 0 ? _b : this.options.sendTimeoutMs,
                    tags,
                    type: 'unmatched',
                };
                this.queuedCommands.push(qc);
                this.sendNextCommand();
            });
            if (this.queuedCommands.length > 0) {
                const cmd = CommandFormatter.format(data, this.options.isBinary);
                const strTags = tags.length > 0 ? ` [${tags.join(', ')}]` : '';
                this.driverLog.info(`queued${strTags}: ${cmd}`);
            }
            return promise;
        });
    }
    sendNextCommand(command) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this._isClosed || this.currentCommand)
                return;
            if (this._suspendType === 'all')
                return;
            if (command === undefined) {
                command = this.queuedCommands.shift();
                this.retryAttempts = 0;
            }
            if (!command)
                return;
            if ('match' in command) {
                this.currentCommand = command;
                this.sendTimeout.set(this.sendTimeoutCallback, command.sendTimeoutMs);
                // If sendTimeoutMs has been overridden for this command and the idle timeout will
                // expire before the send timeout, then increase the idle timeout, but also limit the
                // extension of the idle timeout so it can't be defeated by continuously sending
                // commands with sendTimeoutMs overrides.
                const now = Date.now();
                const maxIdleTime = this.options.idleTimeoutMs + 3 * command.sendTimeoutMs;
                if (this._suspendType === undefined &&
                    this.options.idleTimeoutMs > 0 &&
                    command.sendTimeoutMs > this.options.sendTimeoutMs &&
                    this.idleTimeout.expires - now < command.sendTimeoutMs &&
                    now - this.lastReceivedTime < maxIdleTime) {
                    const idleTimeoutMs = command.sendTimeoutMs + 1.1 * this.options.sendTimeoutMs;
                    this.idleTimeout.set(this.onIdleTimeout, idleTimeoutMs);
                }
            }
            const cmd = `${command.address}:${command.port}: ` +
                CommandFormatter.format(command.command, this.options.isBinary);
            const strTags = command.tags.length > 0 ? ` [${command.tags.join(', ')}]` : '';
            this.driverLog.info(`sending${strTags}: ${cmd}`);
            try {
                yield this.socket.send(command.address, command.port, command.command);
                if (!('match' in command)) {
                    command.resolve();
                    this.timeouts.set(this.sendNextCommand); // prevent deep nesting
                }
            }
            catch (error) {
                this.log.warning(`send failed: ${error.message}`);
                this.sendTimeout.clear();
                this.currentCommand = undefined;
                this.timeouts.set(this.sendNextCommand); // prevent deep nesting
                command.reject(new TransportError(error.message, 'sendFailed'));
            }
        });
    }
    sendTimeoutCallback() {
        if (this.currentCommand === undefined)
            return;
        const qc = this.currentCommand;
        this.currentCommand = undefined;
        const message = `timeout waiting for response ${JSON.stringify(qc.tags)}`;
        this.log.warning(message);
        if (this.retryAttempts < qc.retryAttempts) {
            this.retryAttempts++;
            this.log.info('retrying command');
            this.sendNextCommand(qc);
        }
        else {
            qc.reject(new TransportError(message, 'sendTimeout'));
            this.sendNextCommand();
        }
    }
    setBroadcast(enabled) {
        return __awaiter(this, void 0, void 0, function* () {
            this.log.info(`${enabled ? 'enabling' : 'disabling'} broadcast`);
            return this.socket.setBroadcast(enabled);
        });
    }
    suspendTimeoutCallback() {
        this._suspendType = undefined;
        this.timeouts.set(this.sendNextCommand);
        this.timeouts.set(this.processReceivedMessages);
        if (this.options.idleTimeoutMs > 0) {
            this.idleTimeout.set(this.onIdleTimeout, this.options.idleTimeoutMs);
        }
    }
    setIdleTimeoutMs(idleTimeoutMs) {
        if (idleTimeoutMs === this.options.idleTimeoutMs)
            return;
        this.options.idleTimeoutMs = idleTimeoutMs;
        if (this.idleTimeout.isRunning) {
            this.idleTimeout.clear();
            if (this.options.idleTimeoutMs > 0) {
                this.idleTimeout.set(this.onIdleTimeout, this.options.idleTimeoutMs);
            }
        }
    }
    suspend(delayMs, suspendType) {
        this.idleTimeout.clear();
        this.suspendTimeout.clear();
        this._suspendType = suspendType;
        if (delayMs === undefined) {
            // suspend indefinitely
        }
        else if (delayMs <= 0) {
            this.log.error(`suspend: delayMs must be > 0`);
            this.resume();
        }
        else {
            // suspend for specified delayMs
            this.suspendTimeout.set(this.suspendTimeoutCallback, delayMs);
        }
    }
    unregisterAsync(id) {
        if (id in this.asyncHandlers) {
            delete this.asyncHandlers[id];
            return true;
        }
        return false;
    }
}
